import React from 'react';
import { caseStudies } from '../data/mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, TrendingUp, Users, Clock } from 'lucide-react';

const CaseStudies = () => {
  const bgColor = '#1a2332';
  const accentColor = '#d4af37';

  return (
    <section id="caseStudies" className="py-20 text-white" style={{ backgroundColor: bgColor }}>
      <div className="container mx-auto px-6">
        <div className="mb-16 text-center">
          <h2 className="mb-4 font-serif text-4xl font-bold md:text-5xl">Success Stories</h2>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accentColor }}></div>
          <p className="mx-auto mt-6 max-w-3xl text-lg text-white opacity-90">
            Transformative recruitment solutions that drive measurable business impact
          </p>
        </div>

        <Tabs defaultValue="case-1" className="mx-auto max-w-6xl">
          <TabsList className="mb-8 grid w-full grid-cols-2 gap-4 bg-transparent md:grid-cols-4">
            {caseStudies.map((study, index) => (
              <TabsTrigger
                key={study.id}
                value={`case-${study.id}`}
                className="rounded-lg border-2 border-white/20 bg-white/5 px-4 py-3 text-sm font-medium text-white backdrop-blur-sm transition-all data-[state=active]:border-current hover:bg-white/10"
              >
                <span className="hidden md:inline">{study.domain}</span>
                <span className="md:hidden">Case {index + 1}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {caseStudies.map((study) => (
            <TabsContent key={study.id} value={`case-${study.id}`}>
              <Card className="border-none bg-white/5 backdrop-blur-sm">
                <CardHeader>
                  <div className="mb-4">
                    <Badge
                      className="mb-3 border-none px-3 py-1 text-sm font-semibold"
                      style={{ backgroundColor: accentColor, color: bgColor }}
                    >
                      {study.domain}
                    </Badge>
                  </div>
                  <CardTitle className="mb-2 font-serif text-3xl font-bold text-white">
                    {study.title}
                  </CardTitle>
                  <p className="text-base text-white opacity-70">{study.client}</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-800/50 p-6 md:grid-cols-4">
                    <div className="text-center">
                      <Users className="mx-auto mb-2 h-6 w-6 text-white" />
                      <div className="text-2xl font-bold text-white">
                        {study.metrics.team}
                      </div>
                      <div className="text-xs font-medium" style={{ color: accentColor }}>Team Size</div>
                    </div>
                    <div className="text-center">
                      <Clock className="mx-auto mb-2 h-6 w-6 text-white" />
                      <div className="text-2xl font-bold text-white">
                        {study.metrics.duration}
                      </div>
                      <div className="text-xs font-medium" style={{ color: accentColor }}>Duration</div>
                    </div>
                    <div className="text-center">
                      <TrendingUp className="mx-auto mb-2 h-6 w-6 text-white" />
                      <div className="text-2xl font-bold text-white">
                        {study.metrics.placements}
                      </div>
                      <div className="text-xs font-medium" style={{ color: accentColor }}>Placements</div>
                    </div>
                    <div className="text-center">
                      <CheckCircle className="mx-auto mb-2 h-6 w-6 text-white" />
                      <div className="text-2xl font-bold text-white">
                        {study.metrics.retention || study.metrics.compliance || study.metrics.savings || study.metrics.conversion}
                      </div>
                      <div className="text-xs font-medium" style={{ color: accentColor }}>Success Rate</div>
                    </div>
                  </div>

                  {/* Challenge */}
                  <div>
                    <h4 className="mb-2 text-lg font-semibold" style={{ color: accentColor }}>
                      Challenge
                    </h4>
                    <p className="leading-relaxed text-white/90">{study.challenge}</p>
                  </div>

                  {/* Solution */}
                  <div>
                    <h4 className="mb-2 text-lg font-semibold" style={{ color: accentColor }}>
                      Solution
                    </h4>
                    <p className="leading-relaxed text-white/90">{study.solution}</p>
                  </div>

                  {/* Results */}
                  <div>
                    <h4 className="mb-3 text-lg font-semibold" style={{ color: accentColor }}>
                      Results Delivered
                    </h4>
                    <ul className="space-y-2">
                      {study.results.map((result, index) => (
                        <li key={index} className="flex gap-3">
                          <CheckCircle
                            className="mt-0.5 h-5 w-5 flex-shrink-0"
                            style={{ color: accentColor }}
                          />
                          <span className="leading-relaxed text-white/90">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default CaseStudies;
